import React, { useState, useEffect } from 'react';
import { Tabs } from "@shared/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ChevronDown, Zap, User } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUtilityInfo, useConsumerWebLogin } from '../../hooks';
import { setAuthToken, getAuthToken } from '@shared/auth/authUtils';
import { toast } from '@shared/hooks/use-toast';
import { fetchNscConfigIfNeeded } from '@features/auth/hooks';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

export const Login = () => {
  const { t } = useTranslation();

  const emailFields: FormField[] = [
    { name: 'email', label: t("Email Address"), type: 'email', required: true, placeholder: t("e.g. michael.johnson@email.com"), defaultValue: '' },
    { name: 'password', label: t("Password"), type: 'password', required: true, placeholder: t("••••••••••••••••"), defaultValue: '' },
  ];

  const phoneFields: FormField[] = [
    { name: 'phone', label: t("Phone Number"), type: 'phone', required: true, placeholder: t("e.g. +1 234 567 8900"), defaultValue: '' },
    { name: 'password', label: t("Password"), type: 'password', required: true, placeholder: t("••••••••••••••••"), defaultValue: '' },
  ];

  const consumerFields: FormField[] = [
    { name: 'consumerNumber', label: t("Consumer Number"), type: 'text', required: true, placeholder: t("e.g. 123456789"), defaultValue: '' },
    { name: 'password', label: t("Password"), type: 'password', required: true, placeholder: t("••••••••••••••••"), defaultValue: '' },
  ];
  const emailForm = FormService.createForm({ fields: emailFields, mode: "onChange", validateOnMount: false });
  const phoneForm = FormService.createForm({ fields: phoneFields, mode: "onChange", validateOnMount: false });
  const consumerForm = FormService.createForm({ fields: consumerFields, mode: "onChange", validateOnMount: false });
  
  const navigate = useNavigate();
  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  
  useEffect(() => {
    if (tenant) {
      localStorage.setItem('tenant', tenant);
    }
    if (utilityName) {
      localStorage.setItem('utilityName', utilityName);
    }
    
    if (getAuthToken()) {
      navigate('/dashboard', { replace: true });
    }
  }, [tenant, utilityName, navigate]);
  
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  useEffect(() => {
    if (utilityInfo?.result?.id) {
      localStorage.setItem('remoteUtilityId', utilityInfo.result.id.toString());
    }
  }, [utilityInfo?.result?.id]);

  const brandingData = utilityInfo?.result?.branding || {};
  const portalBranding = brandingData?.portalBranding || {};
  const loginBranding = brandingData?.loginBranding || {};

  const logoUrl = portalBranding?.logo || null;
  const brandText = portalBranding?.brandText || null;
  
  let backgroundClass = "bg-gradient-to-br from-[#0a1526] via-[#103063] to-[#1e52a8]";
  let backgroundStyle: React.CSSProperties = {};
  
  if (loginBranding?.backgroundImage) {
    backgroundClass = "bg-cover bg-center";
    backgroundStyle = { backgroundImage: `url(${loginBranding.backgroundImage})` };
  } else if (loginBranding?.preset) {
    switch (loginBranding.preset) {
      case "ocean_blue":
        backgroundClass = "bg-gradient-to-br from-blue-600 to-violet-600";
        break;
      case "violet_pink":
        backgroundClass = "bg-gradient-to-br from-purple-400 to-pink-400";
        break;
      case "teal_sky":
        backgroundClass = "bg-gradient-to-br from-teal-400 to-cyan-400";
        break;
      case "sunset":
        backgroundClass = "bg-gradient-to-br from-orange-400 to-rose-400";
        break;
      case "forest":
        backgroundClass = "bg-gradient-to-br from-emerald-600 to-teal-800";
        break;
      case "navy_solid":
        backgroundClass = "bg-[#0f172a]";
        break;
      default:
        backgroundClass = "bg-gradient-to-br from-[#0a1526] via-[#103063] to-[#1e52a8]";
    }
  }

  const displayUtilityName = utilityInfo?.result?.name || 'SMART360';

   const storageData = getLoginDataFromStorage();
   const [remoteConsumerNumber, setRemoteConsumerNumber] = useState<string | null>(
        storageData.remoteConsumerNumber || localStorage.getItem('remoteConsumerNumber')
      );

  const [rememberMe, setRememberMe] = useState<boolean>(false);
      
          
  const loginMutation = useConsumerWebLogin();

   const getOrSetConsumerNumber = (loginResult?: any) => {
    let consumerNumber = null;
    
    // Try to get from localStorage first
    consumerNumber = localStorage.getItem('remoteConsumerNumber');
    
    if (!consumerNumber) {
      // Try to extract from loginResult
      if (loginResult?.result?.consumer_no) {
        consumerNumber = loginResult.result.consumer_no;
        localStorage.setItem('remoteConsumerNumber', consumerNumber);
      }
      // Try alternative field names
      else if (loginResult?.result?.consumer_number) {
        consumerNumber = loginResult.result.consumer_number;
        localStorage.setItem('remoteConsumerNumber', consumerNumber);
      }
    }
    
    return consumerNumber;
  };

  const onSubmit = (data: any) => {
    const username = data.email || data.phone || data.consumerNumber;
    
    const payload = {
      user: {
        username: username,
        password: data.password
      },
      remote_utility_id: utilityInfo?.result?.id || Number(localStorage.getItem('remoteUtilityId')) || null,
      is_consumer_web: true
    };

    loginMutation.mutate(payload, {
      onSuccess: async (result) => {
        const token = result?.result?.user?.token;
            if (token) {
              setAuthToken(token);
            } else {
              toast({
                title: t("Login Failed"),
                description: t("No authentication token received"),
                variant: "destructive"
              });
              return;
            }
            
            toast({
              title: t("Success!"),
              description: t("Login successful! Redirecting to dashboard..."),
            });
            
            // Store login result
            localStorage.setItem('loginResult', JSON.stringify(result));
            
            // Extract and store consumer number from login response
            const extractedConsumerNumber = getOrSetConsumerNumber(result);
            if (extractedConsumerNumber) {
              setRemoteConsumerNumber(extractedConsumerNumber);
            }
            
            // Store authentication data if needed
            if (rememberMe) {
              localStorage.setItem('rememberMe', 'true');
              localStorage.setItem('username', data.username);
              localStorage.setItem('serviceProvider', data.service_provider);
            }
            
            // Store remote utility ID for dashboard use
            localStorage.setItem('remoteUtilityId', result?.result?.remoteUtilityId.toString());
            
            // **✅ Fetch NSC configuration after successful login**
            await fetchNscConfigIfNeeded(result?.result?.remoteUtilityId.toString());
            
            // Navigate to dashboard - consumer details will be fetched there
            navigate('/dashboard');
      },
      onError: (err: any) => {
        console.error("Login Failed", err);
        const errorMessage = err?.response?.data?.message || err?.message || t("An unexpected error occurred during login");
        toast({
          title: t("Login Failed"),
          description: errorMessage,
          variant: "destructive"
        });
      }
    });
  };

  const handleFieldChange = (formInstance: any, fieldName: string) => {
    if (FormService.getFieldError(formInstance, fieldName)) {
      formInstance.clearErrors(fieldName);
    }
  };

  const handleReset = (formInstance: any) => {
    formInstance.reset();
  };

  const renderFooter = () => (
      <div className="flex flex-col gap-4 mt-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" className="rounded border-gray-300 w-4 h-4 text-blue-600 focus:ring-blue-500" />
          {t("Remember me")}
        </label>
        <button type="button" onClick={() => navigate(tenant && utilityName ? `/forgot-password/${tenant}/${utilityName}` : '/forgot-password')} className="text-sm text-blue-600 hover:underline">{t("Forgot password?")}</button>
      </div>
      <Button type="submit" variant="default" className="w-full ">
        <span className="mr-2">→</span> {t("Sign In")}
      </Button>
      
      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-xs  font-medium">
          <span className="bg-white px-2 text-gray-400">{t("Or")}</span>
        </div>
      </div>

      <Button type="button" variant="outline" className="" onClick={() => navigate('/')}>
        <User className="mr-2 h-4 w-4" />
        {t("Continue as Guest")}
      </Button>
      
      <div className="text-center text-sm text-gray-600 mt-2">
        {t("New user?")} <button type="button" onClick={() => navigate(tenant && utilityName ? `/create-account/${tenant}/${utilityName}` : '/create-account')} className="text-blue-600 font-semibold hover:underline">{t("Create an account")}</button>
      </div>
    </div>
  );

  const tabComponents = {
    email: {
      label: t("Email"),
      shortLabel: t("Email"),
      component: (
        <DynamicForm 
          fields={emailFields} 
          form={emailForm} 
          onSubmit={onSubmit}
          onReset={() => handleReset(emailForm)}
          onFieldChange={(fieldName) => handleFieldChange(emailForm, fieldName)}
          config={{ classes: FIELD_CLASSES }}
          renderCustomFooter={renderFooter}
        />
      )
    },
    phone: {
      label: "Phone No",
      shortLabel: "Phone",
      component: (
        <DynamicForm 
          fields={phoneFields} 
          form={phoneForm} 
          onSubmit={onSubmit}
          onReset={() => handleReset(phoneForm)}
          onFieldChange={(fieldName) => handleFieldChange(phoneForm, fieldName)}
          config={{ classes: FIELD_CLASSES }}
          renderCustomFooter={renderFooter}
        />
      )
    },
    consumer: {
      label: "Consumer No",
      shortLabel: "Consumer",
      component: (
        <DynamicForm 
          fields={consumerFields} 
          form={consumerForm} 
          onSubmit={onSubmit}
          onReset={() => handleReset(consumerForm)}
          onFieldChange={(fieldName) => handleFieldChange(consumerForm, fieldName)}
          config={{ classes: FIELD_CLASSES }}
          renderCustomFooter={renderFooter}
        />
      )
    }
  };

  return (
    <div className={`min-h-screen w-full flex flex-col items-center justify-center p-4 font-sans ${backgroundClass}`} style={backgroundStyle}>
      
      {/* Logo Area */}
      <div className="mb-8 flex flex-col items-center">
        <div className="flex items-center gap-3 text-white">
          {logoUrl ? (
            <img src={logoUrl} alt={displayUtilityName} className="h-10 object-contain rounded-lg bg-white/10 p-1" />
          ) : brandText ? (
            <div className="font-bold tracking-widest text-2xl leading-tight text-center uppercase">
              {brandText}
            </div>
          ) : null}
        </div>
      </div>

      {/* Outer Grey Wrapper */}
      <div className="bg-[#F8F9FA] rounded-xl w-full max-w-[450px] p-4 md:p-6 shadow-2xl border border-white/20">
        
        {/* Main Login Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full p-6 md:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t("Welcome back")}</h1>
            <p className="text-sm text-gray-500">{t("Sign in to manage your utility account")}</p>
          </div>

          <Tabs 
            defaultValue="email"
            onValueChange={() => {
              emailForm.reset();
              phoneForm.reset();
              consumerForm.reset();
            }}
            tabComponents={tabComponents}
            tabsListClassName="grid grid-cols-3 w-full bg-gray-50 p-1 rounded-lg h-12"
            idPrefix="login-tab"
            className="w-full"
          />
        </div>
        
        {/* Help Section */}
        <div className="mt-4 w-full">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="help" className="border-none bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-800 [&>svg]:text-gray-400 [&>svg]:h-5 [&>svg]:w-5 data-[state=open]:border-b data-[state=open]:border-gray-100">
                {t("Need Help Signing In?")}
              </AccordionTrigger>
              <AccordionContent className="p-4 bg-white">
                <Accordion type="single" collapsible className="flex flex-col gap-3">
                  <AccordionItem value="q1" className="border-none">
                    <AccordionTrigger className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-gray-700 hover:no-underline data-[state=open]:border-black data-[state=open]:border data-[state=open]:bg-white border border-transparent transition-all text-left">
                      {t('auth.login.faq1Question')}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pt-3 pb-0 text-gray-500 text-sm leading-relaxed">
                      {t('auth.login.faq1Answer')}
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="q2" className="border-none">
                    <AccordionTrigger className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-gray-700 hover:no-underline data-[state=open]:border-black data-[state=open]:border data-[state=open]:bg-white border border-transparent transition-all text-left">
                      {t('auth.login.faq2Question')}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pt-3 pb-0 text-gray-500 text-sm leading-relaxed">
                      {t('auth.login.faq2Answer')}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

      </div>
      
      {/* Footer */}
      {/* <div className="mt-8 text-white/50 text-xs text-center">
        © 2024 City of Lakewood Water & Sewer Authority
      </div> */}
    </div>
  );
};
